import equal from "fast-deep-equal/es6";

import { GroupClient } from "@formant/core/client/GroupClient";
import { defined } from "@formant/core/common/defined";
import { IGroup } from "@formant/core/model/IGroup";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { IGroupStore } from "./IGroupStore";
import { StoreCache } from "./StoreCache";

export class GroupStore implements IGroupStore {
    private listCache = new StoreCache<undefined, IGroup[]>({
        isEqual: equal,
    });
    private cache = new StoreCache<Uuid, IGroup | false>({
        isEqual: equal,
    });

    constructor(
        private groupClient: GroupClient,
        private authStore: IAuthStore,
    ) {}

    list(): IGroup[] | undefined {
        return this.listAll()?.filter(_ => _.enabled);
    }

    listAll(): IGroup[] | undefined {
        return this.listCache.get(
            undefined,
            async () => await this.fetchList(),
        );
    }

    get(id: Uuid): IGroup | false | undefined {
        return this.cache.get(
            id,
            async () => (await this.fetchGet(id)) || false,
        );
    }

    async create(group: IGroup): Promise<IGroup> {
        const created = await this.groupClient.create(
            await this.authStore.token(),
            group,
        );
        this.cache.set(defined(created.id), created);
        this.listCache.clear();
        return created;
    }

    async update(id: Uuid, group: Partial<IGroup>) {
        const updated = await this.groupClient.update(
            await this.authStore.token(),
            id,
            group,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    save(group: IGroup): Promise<IGroup> {
        const { id } = group;
        if (id) {
            return this.update(id, group);
        } else {
            return this.create(group);
        }
    }

    async disable(id: Uuid) {
        return await this.update(id, { enabled: false });
    }

    async fetchList(): Promise<IGroup[]> {
        const value = await this.groupClient.list(await this.authStore.token());
        value.forEach(_ => this.cache.set(defined(_.id), _));
        this.listCache.set(undefined, value);
        return value;
    }

    async fetchGet(id: Uuid): Promise<IGroup | false> {
        const group = await this.groupClient.get(
            await this.authStore.token(),
            id,
        );
        const value = group || false;
        this.cache.set(id, value);
        return value;
    }
}
